// Общая проверка "отклонение план/факт ≠ 0 без указанной причины" (NTF.eAk.APPL41.01,
// RQ.eAk.APPL41.36 и аналогичные для финансирования/ЦИ, АС 4) — используется:
//  - для блокировки сохранения карточки ГИП (application_41.vue, saveData);
//  - для подтверждения закрытия карточки при несохранённых данных/отклонениях без причины
//    (application_41.vue, onBack, NTF.eAk.APPL41.04);
//  - для подсветки конкретных полей на вкладках "Общие сведения"/"Целевые индикаторы"
//    (genView.vue / targetIndicators.vue).

export function toNumber(v: any): number {
    if (v === null || v === undefined || v === '') return 0;
    const n = Number(String(v).replace(/\s|%/g, '').replace(',', '.'));
    return isNaN(n) ? 0 : n;
}

export function hasUnfilledReason(reason: any): boolean {
    return reason === null || reason === undefined || String(reason).trim() === '';
}

export function hasValue(v: any): boolean {
    return v !== null && v !== undefined && v !== '';
}

// Отклонение факт - план; при отсутствии факта не считается (RQ.eAk.APPL41.14/19/26 —
// "при отсутствии факта отклонение не рассчитывается"). null означает "не считается",
// а не "нет отклонения" — вызывающий код не должен путать его с 0.
export function deviation(plan: any, fact: any): number | null {
    if (!hasValue(fact)) return null;
    return toNumber(fact) - toNumber(plan);
}

// 0 — вкладка "Общие сведения" (genView), 1 — "Целевые индикаторы" (targetIndicators)
export interface DeviationIssue {
    tab: 0 | 1;
    path: string;
}

// Период реализации: план (gipForm.impl_period, формат "YYYY - YYYY") vs факт
// (bip_form_info.impl_period.fact_from/fact_to). Пока факт не заполнен — сравнивать не с чем.
function checkImplPeriod(gipForm: any): DeviationIssue[] {
    const info = gipForm && gipForm.bip_form_info && gipForm.bip_form_info.impl_period;
    if (!info) return [];
    const { fact_from, fact_to, deviation_reason } = info;
    if (!fact_from && !fact_to) return [];
    const planParts = String(gipForm.impl_period || '').split('-').map((s: string) => s.trim());
    const [planFrom, planTo] = planParts;
    const deviates = (!!fact_from && String(fact_from) !== planFrom) || (!!fact_to && String(fact_to) !== planTo);
    if (deviates && hasUnfilledReason(deviation_reason)) {
        return [{ tab: 0, path: 'bip_form_info.impl_period' }];
    }
    return [];
}

// Финансирование: РБ/МБ — план/факт по источнику (gipForm.financing), причина по источнику
// (bip_form_info.financing.deviation_reason).
function checkFinancing(gipForm: any): DeviationIssue[] {
    const financing = gipForm && gipForm.financing;
    if (!financing) return [];
    const reasons = (gipForm.bip_form_info && gipForm.bip_form_info.financing && gipForm.bip_form_info.financing.deviation_reason) || {};
    const issues: DeviationIssue[] = [];
    for (const src of Object.keys(financing)) {
        const plan = toNumber(financing[src] && financing[src].plan);
        const fact = toNumber(financing[src] && financing[src].fact);
        if (fact - plan !== 0 && hasUnfilledReason(reasons[src])) {
            issues.push({ tab: 0, path: `financing.${src}` });
        }
    }
    return issues;
}

// Целевые индикаторы: доля/факт ЦИ (ti.plan/ti.fact + ti.deviation_reason) и конечные
// результаты по годам (res.years[].plan/fact/deviation_reason).
function checkTargetIndicators(gipForm: any): DeviationIssue[] {
    const list = gipForm && gipForm.target_indicators;
    if (!Array.isArray(list)) return [];
    const issues: DeviationIssue[] = [];
    list.forEach((ti: any, tiIdx: number) => {
        const dev = deviation(ti.plan, ti.fact);
        if (dev !== null && dev !== 0 && hasUnfilledReason(ti.deviation_reason)) {
            issues.push({ tab: 1, path: `target_indicators[${tiIdx}]` });
        }
        (ti.final_results || []).forEach((res: any, resIdx: number) => {
            // RQ.eAk.APPL41.26/.27 — отклонение конечного результата не считается без
            // факта, причина обязательна только при отклонении ≠ 0.
            (res.years || []).forEach((y: any, yIdx: number) => {
                const yDev = deviation(y.plan, y.fact);
                if (yDev !== null && yDev !== 0 && hasUnfilledReason(y.deviation_reason)) {
                    issues.push({ tab: 1, path: `target_indicators[${tiIdx}].final_results[${resIdx}].years[${yIdx}]` });
                }
            });
        });
    });
    return issues;
}

// Строка года конечного результата заполнена (есть план, факт или причина), но год не выбран —
// год обязателен (п. 4.2 постановки), сохранять такую строку нельзя.
export function isYearMissing(y: any): boolean {
    if (!y || hasValue(y.year)) return false;
    return hasValue(y.plan) || hasValue(y.fact) || !hasUnfilledReason(y.deviation_reason);
}

function checkResultYears(gipForm: any): DeviationIssue[] {
    const list = gipForm && gipForm.target_indicators;
    if (!Array.isArray(list)) return [];
    const issues: DeviationIssue[] = [];
    list.forEach((ti: any, tiIdx: number) => {
        (ti.final_results || []).forEach((res: any, resIdx: number) => {
            (res.years || []).forEach((y: any, yIdx: number) => {
                if (isYearMissing(y)) {
                    issues.push({ tab: 1, path: `target_indicators[${tiIdx}].final_results[${resIdx}].years[${yIdx}].year` });
                }
            });
        });
    });
    return issues;
}

export function findDeviationIssues(gipForm: any): DeviationIssue[] {
    if (!gipForm) return [];
    return [
        ...checkImplPeriod(gipForm),
        ...checkFinancing(gipForm),
        ...checkTargetIndicators(gipForm),
        ...checkResultYears(gipForm),
    ];
}

// Булевые версии checkImplPeriod/checkFinancing/checkTargetIndicators (та же логика,
// без деталей по конкретным полям) — источник для флк-геттеров, навешиваемых на каждый
// ГИП (application_41.vue, assignFlkProperties): один валидатор флк = одна из этих
// категорий отклонения "план/факт без причины".
export function hasImplPeriodDeviation(gipForm: any): boolean {
    return checkImplPeriod(gipForm).length > 0;
}

export function hasFinancingDeviation(gipForm: any): boolean {
    return checkFinancing(gipForm).length > 0;
}

export function hasTargetIndicatorsDeviation(gipForm: any): boolean {
    return checkTargetIndicators(gipForm).length > 0;
}
